import { http } from '@/shared/api/httpClient';
import type {
  AddParticipantByOrganizerRequest,
  ApiBracket,
  ApiCompetition,
  ApiCourt,
  ApiMatch,
  ApiMatchStatus,
  ApiParticipant,
  ApiRoundRobinDraw,
  ApiStanding,
  ClubDashboard,
  CreateCompetitionRequest,
  CreateConsolationRequest,
  UpdateCompetitionPayload,
} from '../model/types';

/** Wywolania endpointow `/api/competitions/**` uzywane przez widoki klubowe. */

/** Pobiera agregat dashboardu klubu (kafelki + postep turniejow). Wymaga MANAGE_CLUB. */
export function getClubDashboard(clubId: string): Promise<ClubDashboard> {
  return http.get<ClubDashboard>(`/competitions/club/${clubId}/dashboard`);
}

export interface ClubMatchesFilter {
  /** Data w formacie ISO (yyyy-MM-dd) — np. plan dnia. */
  date?: string;
  /** Status meczu — np. `IN_PROGRESS` dla meczow na zywo. */
  status?: ApiMatchStatus;
}

/** Pobiera mecze wszystkich rozgrywek klubu (chronologicznie). Wymaga MANAGE_CLUB. */
export function getClubMatches(
  clubId: string,
  filter: ClubMatchesFilter = {},
): Promise<ApiMatch[]> {
  return http.get<ApiMatch[]>(`/competitions/club/${clubId}/matches`, {
    query: { date: filter.date, status: filter.status },
  });
}

/**
 * Pobiera wszystkie rozgrywki klubu — razem ze szkicami i anulowanymi.
 * Backend zwraca cala liste naraz, posortowana malejaco po dacie modyfikacji.
 */
export function getClubCompetitions(clubId: string): Promise<ApiCompetition[]> {
  return http.get<ApiCompetition[]>('/competitions', { query: { clubId } });
}

/**
 * Zaklada nowa rozgrywke. Powstaje w statusie DRAFT, o ile `clubId` jest podany —
 * bez niego backend ustawia AWAITING_PAYMENT.
 *
 * Odpowiedz to 201 z pustym cialem i naglowkiem `Location`; klient HTTP naglowkow
 * nie udostepnia, wiec identyfikator odczytujemy dopiero z odswiezonej listy.
 */
export function createCompetition(request: CreateCompetitionRequest): Promise<void> {
  return http.post<void>('/competitions', request);
}

/** Pobiera pojedyncza rozgrywke. */
export function getCompetition(competitionId: string): Promise<ApiCompetition> {
  return http.get<ApiCompetition>(`/competitions/${competitionId}`);
}

/** Pobiera zgloszenia do rozgrywki — jeden rekord na slot (para dzieli slot). */
export function getParticipants(competitionId: string): Promise<ApiParticipant[]> {
  return http.get<ApiParticipant[]>(`/competitions/${competitionId}/participants`);
}

/**
 * Dodaje slot w imieniu organizatora.
 *
 * Kazda strona slotu to albo `userId` istniejacego gracza, albo dane goscia —
 * gosc dostaje konto INVITED w user-service i event zaproszenia.
 * Wymaga statusu REGISTRATION_OPEN (inaczej 409).
 */
export function addParticipantByOrganizer(
  competitionId: string,
  request: AddParticipantByOrganizerRequest,
): Promise<void> {
  return http.post<void>(`/competitions/${competitionId}/participants/by-organizer`, request);
}

/** Usuwa zgloszenie. Dozwolone wylacznie przed startem rozgrywki. */
export function removeParticipant(competitionId: string, userId: string): Promise<void> {
  return http.delete<void>(`/competitions/${competitionId}/participants/${userId}`);
}

/** Ustawia rozstawienie zawodnika przed losowaniem. */
export function setParticipantSeed(
  competitionId: string,
  userId: string,
  seed: number | null,
): Promise<void> {
  return http.put<void>(`/competitions/${competitionId}/participants/${userId}/seed`, { seed });
}

/**
 * Przejscia cyklu zycia rozgrywki dostepne z listy turniejow.
 * Kazde wymaga uprawnien organizatora — backend odpowiada 403, gdy ich brak.
 */
export type CompetitionLifecycleAction =
  'open-registration' | 'close-registration' | 'start' | 'complete';

/** Wykonuje przejscie cyklu zycia rozgrywki (`POST /api/competitions/{id}/{akcja}`). */
export function runCompetitionAction(
  competitionId: string,
  action: CompetitionLifecycleAction,
): Promise<void> {
  return http.post<void>(`/competitions/${competitionId}/${action}`);
}

/** Anuluje rozgrywke (`POST /api/competitions/{id}/cancel`) — status CANCELLED, dalej widoczna. */
export function cancelCompetition(competitionId: string): Promise<void> {
  return http.post<void>(`/competitions/${competitionId}/cancel`);
}

/**
 * Miekko usuwa rozgrywke (`DELETE /api/competitions/{id}`) — znika ze wszystkich
 * widokow, wiersz zostaje w bazie. Operacji nie da sie cofnac przez API.
 */
export function deleteCompetition(competitionId: string): Promise<void> {
  return http.delete<void>(`/competitions/${competitionId}`);
}

/**
 * Kopiuje rozgrywke pod nowa nazwa (`POST /api/competitions/{id}/copy`).
 *
 * Kopia przejmuje wszystkie ustawienia i sklad oryginalu (bez wycofanych) i zawsze
 * powstaje w statusie `REGISTRATION_OPEN`. Backend odrzuca kopiowanie rozgrywki,
 * ktora nie doszla jeszcze do otwarcia zapisow, oraz anulowanej (409).
 *
 * Odpowiedz to 201 z pustym cialem i naglowkiem `Location`; klient HTTP naglowkow
 * nie udostepnia, wiec kopie odczytujemy dopiero z odswiezonej listy.
 */
export function copyCompetition(competitionId: string, name: string): Promise<void> {
  return http.post<void>(`/competitions/${competitionId}/copy`, { name });
}

/**
 * Tworzy turniej pocieszenia (`POST /api/competitions/{id}/consolation`).
 *
 * Skladem zostaja przegrani 1. rundy turnieju zasadniczego, sparowani
 * w kolejnosci meczow (bez losowania); turniej startuje od razu w statusie
 * IN_PROGRESS z gotowa drabinka. Backend odrzuca (409) turniej bez systemu
 * PLAYOFF, z niezakonczona 1. runda albo z mniej niz 2 przegranymi.
 *
 * Odpowiedz to 201 z pustym cialem i naglowkiem `Location`; klient HTTP
 * naglowkow nie udostepnia, wiec nowy turniej odczytujemy z odswiezonej listy.
 */
export function createConsolation(
  competitionId: string,
  request: CreateConsolationRequest,
): Promise<void> {
  return http.post<void>(`/competitions/${competitionId}/consolation`, request);
}

/**
 * Aktualizuje ustawienia rozgrywki (`PUT /api/competitions/{id}`).
 *
 * Dozwolone do momentu losowania; zmiana typu meczu lub dyscypliny wymaga
 * pustej listy zgloszen (409). Pola `null` backend pomija.
 */
export function updateCompetition(
  competitionId: string,
  payload: UpdateCompetitionPayload,
): Promise<void> {
  return http.put<void>(`/competitions/${competitionId}`, payload);
}

/**
 * Zmienia widocznosc rozgrywki (`PUT /api/competitions/{id}/visibility`).
 *
 * W odroznieniu od ogolnej edycji dozwolone w kazdym statusie — takze po
 * losowaniu i po zakonczeniu. PRIVATE natychmiast ukrywa rozgrywke z ekranu
 * publicznego klubu, PUBLIC ja przywraca.
 */
export function setCompetitionVisibility(
  competitionId: string,
  visibility: 'PUBLIC' | 'PRIVATE',
): Promise<void> {
  return http.put<void>(`/competitions/${competitionId}/visibility`, { visibility });
}

/**
 * Przeprowadza losowanie rozgrywki (`POST /api/competitions/{id}/draw`).
 *
 * Backend wybiera algorytm po systemie rozgrywek: PLAYOFF — drabinka,
 * SINGLE_GROUP — kazdy z kazdym, GROUP_PLAYOFF — faza grupowa. Wymaga statusu
 * REGISTRATION_CLOSED i samo przenosi rozgrywke w IN_PROGRESS.
 */
export function drawCompetition(competitionId: string): Promise<ApiBracket | ApiRoundRobinDraw> {
  return http.post<ApiBracket | ApiRoundRobinDraw>(`/competitions/${competitionId}/draw`);
}

/**
 * Losuje faze finalowa GROUP_PLAYOFF (`POST /api/competitions/{id}/advance`).
 *
 * Dostepne dopiero po rozstrzygnieciu wszystkich meczow grupowych; ksztalt
 * odpowiedzi zalezy od `secondPhaseType` (drabinka albo grupa finalowa).
 */
export function advanceToFinalPhase(
  competitionId: string,
): Promise<ApiBracket | ApiRoundRobinDraw> {
  return http.post<ApiBracket | ApiRoundRobinDraw>(`/competitions/${competitionId}/advance`);
}

/** Pobiera pelna drabinke pucharowa (`GET /api/competitions/{id}/bracket`). */
export function getBracket(competitionId: string): Promise<ApiBracket> {
  return http.get<ApiBracket>(`/competitions/${competitionId}/bracket`);
}

/** Pobiera wszystkie mecze rozgrywki (`GET /api/matches?competitionId=…`). */
export function getCompetitionMatches(competitionId: string): Promise<ApiMatch[]> {
  return http.get<ApiMatch[]>('/matches', { query: { competitionId } });
}

/** Pobiera pojedynczy mecz (`GET /api/matches/{id}`). */
export function getMatch(matchId: string): Promise<ApiMatch> {
  return http.get<ApiMatch>(`/matches/${matchId}`);
}

/** Pobiera korty turniejowe rozgrywki (`GET /api/competitions/{id}/courts`). */
export function getCompetitionCourts(competitionId: string): Promise<ApiCourt[]> {
  return http.get<ApiCourt[]>(`/competitions/${competitionId}/courts`);
}

/**
 * Pobiera tabele rozgrywki (`GET /api/competitions/{id}/standings`) — wszystkie
 * grupy naraz; przeliczane po każdym rozstrzygniętym meczu.
 */
export function getStandings(competitionId: string): Promise<ApiStanding[]> {
  return http.get<ApiStanding[]>(`/competitions/${competitionId}/standings`);
}

/** Wynik jednego seta w ładunku `EnterScoreRequest`. */
export interface SetScorePayload {
  setNumber: number;
  score1: number;
  score2: number;
  tiebreakScore1: number | null;
  tiebreakScore2: number | null;
}

/**
 * Wpisuje wynik meczu (`POST /api/matches/{id}/score`).
 *
 * Dozwolone dla uczestnika meczu albo zarządzającego rozgrywką (uprawnienie
 * klubowe MANAGE_CLUB). Mecz przechodzi w COMPLETED, a zwycięzca awansuje
 * w drabince.
 */
export function enterMatchScore(matchId: string, scores: SetScorePayload[]): Promise<void> {
  return http.post<void>(`/matches/${matchId}/score`, { scores });
}

/**
 * Poprawia wpisany wynik (`PUT /api/matches/{id}/score`) — tylko zarządzający
 * rozgrywką. Korekta drabinkowa jest odrzucana (409), gdy kolejna runda już
 * ruszyła.
 */
export function editMatchScore(matchId: string, scores: SetScorePayload[]): Promise<void> {
  return http.put<void>(`/matches/${matchId}/score`, { scores });
}

/** Orzeka walkower (`POST /api/matches/{id}/walkover`); strona 1 albo 2 wygrywa. */
export function matchWalkover(matchId: string, winningSide: 1 | 2): Promise<void> {
  return http.post<void>(`/matches/${matchId}/walkover`, { winningSide });
}

/**
 * Poprawia walkower meczu już rozstrzygniętego (`PUT /api/matches/{id}/walkover`) —
 * zmienia stronę, której go przyznano, albo zamienia wpisany wynik na walkower
 * (sety są wtedy czyszczone). Tylko zarządzający rozgrywką.
 */
export function editMatchWalkover(matchId: string, winningSide: 1 | 2): Promise<void> {
  return http.put<void>(`/matches/${matchId}/walkover`, { winningSide });
}

/** Akcje statusu meczu bez ciała żądania. */
export type MatchLifecycleAction = 'start' | 'finish' | 'suspend' | 'resume';

/**
 * Zmienia status meczu (`POST /api/matches/{id}/{akcja}`): start —
 * SCHEDULED→IN_PROGRESS, finish — koniec gry bez wyniku (→FINISHED),
 * suspend — przerwa (np. medyczna, deszcz), resume — wznowienie.
 */
export function runMatchAction(matchId: string, action: MatchLifecycleAction): Promise<void> {
  return http.post<void>(`/matches/${matchId}/${action}`);
}
